/**
 * SafetyNow App Shell — Simulink-inspired layout with sidebar navigation.
 *
 * Layout: Collapsible sidebar (left) | Main content area (center)
 * Header: Project name, mode indicator, theme toggle
 * Sidebar: Navigation to all analysis modules
 * Content: Lazy-loaded editor pages via React Router
 */

import React, { Suspense, lazy, useState, useEffect, useRef } from 'react';
import { Routes, Route, NavLink, useLocation } from 'react-router-dom';
import {
  TreePine, GitFork, Workflow, BarChart3, Shield, FileCheck,
  Network, Activity, Boxes, ClipboardCheck, Settings, Moon, Sun,
  PanelLeftClose, PanelLeft, ChevronRight, ChevronDown, Plus, Pencil,
  Archive, RotateCcw, LogOut, Mail,
} from 'lucide-react';
import { useProjectStore } from './stores/projectStore';
import { isApiConnected } from './services/dataService';
import { isLoggedIn, getUser, logout } from './services/authService';
import { AILevelBadge } from './components/AILevelBadge';
import { ScopeLabel } from './components/ScopeLabel';

function CollabPresence() {
  // Lightweight — just shows if WebSocket is connected, no polling
  return null; // Will show user avatars when collab server broadcasts presence
}

function ConnectionBadge() {
  const [online, setOnline] = useState(false);
  useEffect(() => { isApiConnected().then(setOnline).catch(() => {}); const t = setInterval(() => isApiConnected().then(setOnline).catch(() => {}), 15000); return () => clearInterval(t); }, []);
  return online
    ? <span className="px-2 py-0.5 rounded bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 font-medium">Connected</span>
    : <span className="px-2 py-0.5 rounded bg-amber-100 dark:bg-amber-900 text-amber-700 dark:text-amber-300 font-medium">Offline</span>;
}

// Lazy-loaded pages
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const FTAEditorPage = lazy(() => import('./pages/FTAEditorPage'));
const STPAEditorPage = lazy(() => import('./pages/STPAEditorPage'));
const MBSAEditorPage = lazy(() => import('./pages/MBSAEditorPage'));
const FMEAPage = lazy(() => import('./pages/FMEAPage'));
const HazardAssessmentPage = lazy(() => import('./pages/HazardAssessmentPage'));
const EventTreePage = lazy(() => import('./pages/EventTreePage'));
const MarkovPage = lazy(() => import('./pages/MarkovPage'));
const DependenceDiagramPage = lazy(() => import('./pages/DependenceDiagramPage'));
const SafetyCasePage = lazy(() => import('./pages/SafetyCasePage'));
const CompliancePage = lazy(() => import('./pages/CompliancePage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const AdminPage = lazy(() => import('./pages/AdminPage'));
const AssumptionsPage = lazy(() => import('./pages/AssumptionsPage'));
const RequirementsPage = lazy(() => import('./pages/RequirementsPage'));
const CCAPage = lazy(() => import('./pages/CCAPage'));
const FRACASPage = lazy(() => import('./pages/FRACASPage'));
const ComponentsPage = lazy(() => import('./pages/ComponentsPage'));
const IntelligencePage = lazy(() => import('./pages/IntelligencePage'));

// Enterprise ARP 4761A pages (Task 10)
const SystemsManagerPage = lazy(() => import('./pages/SystemsManagerPage'));
const DSRDashboardPage = lazy(() => import('./pages/DSRDashboardPage'));
const DocumentTemplatesPage = lazy(() => import('./pages/DocumentTemplatesPage'));
const AccessControlPage = lazy(() => import('./pages/AccessControlPage'));
const ProjectSetupWizardPage = lazy(() => import('./pages/ProjectSetupWizardPage'));

// Stage 3 UI pages — Sato Dev MBSA 3-view method + cut-set explorer + DAL allocation
const FunctionalViewPage = lazy(() => import('./pages/FunctionalViewPage'));
const ZonalViewPage = lazy(() => import('./pages/ZonalViewPage'));
const CutSetExplorerPage = lazy(() => import('./pages/CutSetExplorerPage'));
const DalAllocationPage = lazy(() => import('./pages/DalAllocationPage'));

// Tenant invitation flow (`.kiro/specs/tenant-invitation-flow/`)
const TeamPage = lazy(() => import('./pages/TeamPage'));
const AcceptInvitePage = lazy(() => import('./pages/AcceptInvitePage'));

// Nav items
interface NavItem {
  path: string;
  icon: React.ComponentType<{ size?: number }>;
  label: string;
  adminOnly?: boolean;
  comingSoon?: boolean;
  tooltip?: string;
}

interface NavSection {
  label: string;
  items: NavItem[];
  collapsedByDefault?: boolean;
}

const NAV_SECTIONS: NavSection[] = [
  {
    label: 'Concept Phase (Phase 0)',
    collapsedByDefault: true,
    items: [
      { path: '#phl',  icon: FileCheck,       label: 'PHL — Preliminary Hazard List',        comingSoon: true,  tooltip: 'List credible hazards for the aircraft type. Seeds AFHA hazard catalog.' },
      { path: '#pha',  icon: FileCheck,       label: 'PHA — Preliminary Hazard Analysis',    comingSoon: true,  tooltip: 'Qualitative severity × likelihood per hazard (MIL-STD-882E style).' },
      { path: '#ft',   icon: Activity,        label: 'Failure Tolerance Analysis',           comingSoon: true,  tooltip: 'N-1 / N-2 / asymmetric failure evaluation for candidate configurations.' },
      { path: '/stpa', icon: GitFork,         label: 'STPA (System-Theoretic Process)',      comingSoon: false, tooltip: 'Control-structure analysis per Leveson (MIT). Applicable at ALL scope levels (aircraft / system / equipment) and all lifecycle phases. Identifies UCAs and loss scenarios.' },
      { path: '#cw',   icon: Shield,          label: 'Crashworthiness Trade',                comingSoon: true,  tooltip: 'Survivable impact envelope analysis. Feeds structural requirements.' },
      { path: '#ts',   icon: ClipboardCheck,  label: 'Trade Study Safety',                   comingSoon: true,  tooltip: 'Aggregates Phase 0 outputs into config recommendation + concept-gate package.' },
    ],
  },
  {
    label: 'Analysis',
    items: [
      { path: '/', icon: BarChart3, label: 'Dashboard' },
      { path: '/fta', icon: TreePine, label: 'Fault Tree (FTA)' },
      { path: '/fmea', icon: ClipboardCheck, label: 'FMEA / FMES' },
      { path: '/cca', icon: Boxes, label: 'CCA' },
      { path: '/mbsa', icon: Network, label: 'MBSA Signal-Flow' },
      { path: '/stpa', icon: GitFork, label: 'STPA' },
      { path: '/event-tree', icon: Workflow, label: 'Event Tree' },
      { path: '/markov', icon: Activity, label: 'Markov' },
      { path: '/rbd', icon: Boxes, label: 'Dependence Diagram' },
    ],
  },
  {
    label: 'Safety Management',
    items: [
      { path: '/hazard-assessment', icon: Shield, label: 'Hazard Assessment' },
      { path: '/safety-case', icon: FileCheck, label: 'Safety Case (GSN)' },
      { path: '/requirements', icon: FileCheck, label: 'Requirements' },
      { path: '/compliance', icon: ClipboardCheck, label: 'Compliance' },
      { path: '/components', icon: Boxes, label: 'Components' },
      { path: '/intelligence', icon: Activity, label: 'Intelligence' },
      { path: '/fracas', icon: Workflow, label: 'FRACAS + MSG-3' },
      { path: '/assumptions', icon: FileCheck, label: 'Assumptions' },
      { path: '/team', icon: Mail, label: 'Team & Invitations', adminOnly: true, tooltip: 'Invite teammates into your tenant and manage their access.' },
      { path: '/admin', icon: Settings, label: 'Admin', adminOnly: true },
    ],
  },
  {
    label: 'Enterprise (ARP 4761A)',
    items: [
      { path: '/new-project', icon: Plus, label: 'New Project' },
      { path: '/systems', icon: Network, label: 'Systems' },
      { path: '/dsrs', icon: FileCheck, label: 'DSRs Dashboard' },
      { path: '/document-templates', icon: ClipboardCheck, label: 'Document Templates' },
      { path: '/access-control', icon: Shield, label: 'Access Control' },
    ],
  },
  {
    label: 'MBSA 3-View (Sato Dev / Dassault)',
    items: [
      { path: '/functional-view', icon: GitFork, label: 'Functional View',
        tooltip: 'Aircraft-level capability tree. Leaf functions become FCs per ARP 4761A §3.1.' },
      { path: '/zonal-view', icon: Network, label: 'Zonal View',
        tooltip: 'Zones + shared hazards (fire, lightning, etc) per ARP 4761A §B.5 ZSA.' },
      { path: '/cut-set-explorer', icon: TreePine, label: 'Cut-Set Explorer',
        tooltip: 'Three result types per ARP 4761A §B: MCS / FFS / Global with DAL filtering.' },
      { path: '/dal-allocation', icon: Shield, label: 'DAL Allocation',
        tooltip: 'Top-down DAL allocation per ARP 4754B §6.2.4 + two-path rule §6.2.5.' },
    ],
  },
];

function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center h-full">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sn-blue-500" />
    </div>
  );
}

class ErrorBoundary extends React.Component<{ children: React.ReactNode }, { error: Error | null }> {
  state = { error: null as Error | null };
  static getDerivedStateFromError(error: Error) { return { error }; }
  render() {
    if (this.state.error) {
      return (
        <div className="flex flex-col items-center justify-center h-full gap-4 p-8">
          <div className="text-red-500 text-lg font-semibold">Page Error</div>
          <div className="text-sm text-slate-600 max-w-md text-center">{this.state.error.message}</div>
          <button onClick={() => { this.setState({ error: null }); window.location.href = '/'; }} className="px-4 py-2 bg-sn-blue-600 text-white rounded text-sm hover:bg-sn-blue-700">Try Again</button>
        </div>
      );
    }
    return this.props.children;
  }
}

function ProjectSwitcher() {
  const { projects, activeProjectId, getActiveProject, createProject, switchProject, renameProject, archiveProject, restoreProject, showArchived, setShowArchived } = useProjectStore();
  const activeList = projects.filter(p => !p.archived);
  const archivedList = projects.filter(p => p.archived);
  const [open, setOpen] = useState(false);
  const [renaming, setRenaming] = useState<string | null>(null);
  const [renameVal, setRenameVal] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const active = getActiveProject();

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen(!open)} className="flex items-center gap-1 text-xs text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white">
        <span className="font-medium">{active?.name ?? 'No project'}</span>
        <ChevronDown size={12} />
      </button>
      {open && (
        <div className="absolute top-8 left-0 z-50 w-72 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg py-1">
          <div className="px-3 py-1.5 text-[10px] font-semibold uppercase text-slate-400 tracking-wider">Active Projects</div>
          {activeList.map(p => (
            <div key={p.id} className={`flex items-center gap-2 px-3 py-1.5 text-xs cursor-pointer ${p.id === activeProjectId ? 'bg-sn-blue-50 dark:bg-slate-700 text-sn-blue-700 dark:text-sn-blue-50 font-medium' : 'text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700'}`}>
              {renaming === p.id ? (
                <input autoFocus value={renameVal} onChange={e => setRenameVal(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { renameProject(p.id, renameVal); setRenaming(null); } if (e.key === 'Escape') setRenaming(null); }}
                  onBlur={() => { renameProject(p.id, renameVal); setRenaming(null); }}
                  className="flex-1 px-1 py-0.5 border border-sn-blue-300 rounded text-xs" />
              ) : (
                <>
                  <span className="flex-1" onClick={() => { if (p.id !== activeProjectId) switchProject(p.id); setOpen(false); }}>{p.name}</span>
                  <button onClick={(e) => { e.stopPropagation(); setRenaming(p.id); setRenameVal(p.name); }} className="p-0.5 hover:text-sn-blue-600" title="Rename"><Pencil size={10} /></button>
                  {activeList.length > 1 && <button onClick={(e) => { e.stopPropagation(); archiveProject(p.id); }} className="p-0.5 hover:text-amber-600" title="Archive"><Archive size={10} /></button>}
                </>
              )}
            </div>
          ))}
          {archivedList.length > 0 && (
            <>
              <div className="border-t border-slate-200 dark:border-slate-700 mt-1 pt-1">
                <button onClick={() => setShowArchived(!showArchived)} className="flex items-center gap-2 px-3 py-1.5 text-[10px] font-semibold uppercase text-slate-400 tracking-wider w-full hover:text-slate-600">
                  <Archive size={10} /> Archived ({archivedList.length})
                  <ChevronDown size={10} className={`ml-auto transition-transform ${showArchived ? 'rotate-180' : ''}`} />
                </button>
              </div>
              {showArchived && archivedList.map(p => (
                <div key={p.id} className="flex items-center gap-2 px-3 py-1.5 text-xs text-slate-400 italic">
                  <span className="flex-1">{p.name}</span>
                  <span className="text-[9px]">{p.archivedAt ? new Date(p.archivedAt).toLocaleDateString() : ''}</span>
                  <button onClick={() => restoreProject(p.id)} className="p-0.5 hover:text-green-600 text-slate-400" title="Restore"><RotateCcw size={10} /></button>
                </div>
              ))}
            </>
          )}
          <div className="border-t border-slate-200 dark:border-slate-700 mt-1 pt-1">
            <button onClick={() => { const name = prompt('Project name:'); if (name) { createProject(name); setOpen(false); } }}
              className="flex items-center gap-2 px-3 py-1.5 text-xs text-sn-blue-600 hover:bg-slate-50 dark:hover:bg-slate-700 w-full">
              <Plus size={12} /> New Project
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export function App() {
  const location = useLocation();

  // Public route: invitees land here from the email link with no auth.
  // Match BEFORE the isLoggedIn() check so an existing session is left
  // intact (the AcceptInvitePage handles its own auth state).
  if (location.pathname.startsWith('/accept-invite/')) {
    return <Suspense fallback={<LoadingSpinner />}><AcceptInvitePage /></Suspense>;
  }

  // Show login page at /login route
  if (location.pathname === '/login') {
    return <Suspense fallback={<LoadingSpinner />}><LoginPage /></Suspense>;
  }

  // Require authentication — no demo mode, no bypass
  if (!isLoggedIn()) {
    return <Suspense fallback={<LoadingSpinner />}><LoginPage /></Suspense>;
  }

  return <AuthenticatedApp />;
}

/** Reads active project's analysisScope and renders the ScopeLabel. */
function ActiveProjectScopeLabel() {
  const activeProject = useProjectStore((s) => s.getActiveProject());
  const scope = (activeProject as any)?.analysisScope;
  if (!scope) return null;
  return (
    <>
      <ChevronRight size={12} className="mx-1 text-slate-400" />
      <ScopeLabel scope={scope} />
    </>
  );
}

/** Renders one sidebar section with collapse support + coming-soon items. */
function NavSectionRenderer({
  section,
  sidebarOpen,
  userRole,
}: {
  section: NavSection;
  sidebarOpen: boolean;
  userRole?: string;
}) {
  const storageKey = `sn:sidebar:${section.label}:collapsed`;
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    const stored = localStorage.getItem(storageKey);
    if (stored !== null) return stored === '1';
    return section.collapsedByDefault ?? false;
  });

  const visibleItems = section.items.filter((item) => !item.adminOnly || userRole === 'admin');
  const toggle = () => {
    const next = !collapsed;
    setCollapsed(next);
    localStorage.setItem(storageKey, next ? '1' : '0');
  };

  return (
    <div className="mb-2">
      {sidebarOpen && (
        <button
          onClick={toggle}
          className="flex items-center gap-1 w-full px-3 py-1 text-[10px] font-semibold uppercase text-slate-400 tracking-wider hover:text-slate-600 dark:hover:text-slate-300"
          aria-expanded={!collapsed}
          aria-controls={`nav-section-${section.label.replace(/\s+/g, '-')}`}
        >
          <ChevronDown
            size={10}
            className={`transition-transform duration-150 ${collapsed ? '-rotate-90' : ''}`}
          />
          <span>{section.label}</span>
        </button>
      )}
      {(!collapsed || !sidebarOpen) && (
        <div id={`nav-section-${section.label.replace(/\s+/g, '-')}`}>
          {visibleItems.map((item) => {
            if (item.comingSoon) {
              return (
                <button
                  key={item.path}
                  type="button"
                  disabled
                  aria-disabled="true"
                  title={item.tooltip ?? 'Coming soon'}
                  className="w-full flex items-center gap-2 px-3 py-1.5 mx-1 rounded text-xs text-slate-400 dark:text-slate-600 italic cursor-not-allowed"
                >
                  <item.icon size={14} />
                  {sidebarOpen && (
                    <>
                      <span className="truncate">{item.label}</span>
                      <span className="ml-auto text-[9px] uppercase tracking-wider text-slate-400/70">
                        soon
                      </span>
                    </>
                  )}
                </button>
              );
            }
            return (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.path === '/'}
                title={item.tooltip}
                className={({ isActive }) =>
                  `flex items-center gap-2 px-3 py-1.5 mx-1 rounded text-xs transition-colors ${
                    isActive
                      ? 'bg-sn-blue-50 dark:bg-slate-800 text-sn-blue-700 dark:text-sn-blue-50 font-medium'
                      : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'
                  }`
                }
              >
                <item.icon size={14} />
                {sidebarOpen && <span>{item.label}</span>}
              </NavLink>
            );
          })}
        </div>
      )}
    </div>
  );
}

function AuthenticatedApp() {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [dark, setDark] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('sn-theme') === 'dark' ||
        (!localStorage.getItem('sn-theme') && window.matchMedia('(prefers-color-scheme: dark)').matches);
    }
    return false;
  });
  const location = useLocation();
  const user = getUser();
  const isViewer = user?.role === 'viewer';

  useEffect(() => {
    document.documentElement.classList.toggle('dark', dark);
    localStorage.setItem('sn-theme', dark ? 'dark' : 'light');
  }, [dark]);

  // projectStore auto-syncs on module load + on 'sn:auth-change' events.
  // This effect adds a safety net: re-sync whenever this App instance mounts
  // (handles post-login navigation without a hard reload).
  useEffect(() => {
    useProjectStore.getState().syncFromApi();
  }, []);

  return (
    <div className="flex h-full">
      {/* Sidebar */}
      <aside className={`${sidebarOpen ? 'w-56' : 'w-12'} flex-shrink-0 bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-800 flex flex-col transition-all duration-200`}>
        {/* Logo */}
        <div className="h-10 flex items-center px-3 border-b border-slate-200 dark:border-slate-800">
          {sidebarOpen ? (
            <span className="font-bold text-sm text-sn-blue-700 dark:text-sn-blue-50">SafetyNow</span>
          ) : (
            <span className="font-bold text-sm text-sn-blue-700 dark:text-sn-blue-50">SN</span>
          )}
          <button onClick={() => setSidebarOpen(!sidebarOpen)} className="ml-auto text-slate-400 hover:text-slate-600 dark:hover:text-slate-300">
            {sidebarOpen ? <PanelLeftClose size={16} /> : <PanelLeft size={16} />}
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto py-2">
          {NAV_SECTIONS.map((section) => (
            <NavSectionRenderer
              key={section.label}
              section={section}
              sidebarOpen={sidebarOpen}
              userRole={user?.role}
            />
          ))}
        </nav>

        {/* Footer */}
        <div className="border-t border-slate-200 dark:border-slate-800 p-2 flex flex-col gap-1">
          {sidebarOpen && user && (
            <div className="px-1 py-1 text-[10px] text-slate-500 truncate">{user.name || user.email}</div>
          )}
          <div className="flex items-center gap-1">
            <button onClick={() => setDark(!dark)} className="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500" title="Toggle theme">
              {dark ? <Sun size={14} /> : <Moon size={14} />}
            </button>
            {sidebarOpen && (
              <NavLink to="/settings" className="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500">
                <Settings size={14} />
              </NavLink>
            )}
            {sidebarOpen && user && (
              <button onClick={() => { logout(); }} className="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500 ml-auto" title="Sign out">
                <LogOut size={14} />
              </button>
            )}
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 flex flex-col overflow-hidden">
        {/* Header bar */}
        <header className="h-10 flex items-center px-4 border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
          <ProjectSwitcher />
          <ActiveProjectScopeLabel />
          <ChevronRight size={12} className="mx-1 text-slate-400" />
          <span className="text-xs text-slate-900 dark:text-white font-medium">
            {NAV_SECTIONS.flatMap(s => s.items).find(i => i.path === location.pathname)?.label || 'SafetyNow'}
          </span>
          <div className="ml-auto flex items-center gap-2 text-xs">
            <AILevelBadge />
            <CollabPresence />
            <ConnectionBadge />
            <span className="text-slate-400">v3.8.1</span>
          </div>
        </header>

        {/* Read-only banner for viewers */}
        {isViewer && (
          <div className="px-4 py-1.5 bg-amber-50 dark:bg-amber-900/30 border-b border-amber-200 dark:border-amber-800 text-amber-700 dark:text-amber-300 text-xs font-medium">
            🔒 Read-only mode — contact an admin to get edit access
          </div>
        )}

        {/* Page content */}
        <div className="flex-1 overflow-hidden">
          <Suspense fallback={<LoadingSpinner />}>
            <ErrorBoundary>
            <Routes>
              <Route path="/" element={<DashboardPage />} />
              <Route path="/fta" element={<FTAEditorPage />} />
              <Route path="/stpa" element={<STPAEditorPage />} />
              <Route path="/mbsa" element={<MBSAEditorPage />} />
              <Route path="/fmea" element={<FMEAPage />} />
              <Route path="/hazard-assessment" element={<HazardAssessmentPage />} />
              <Route path="/event-tree" element={<EventTreePage />} />
              <Route path="/markov" element={<MarkovPage />} />
              <Route path="/rbd" element={<DependenceDiagramPage />} />
              <Route path="/safety-case" element={<SafetyCasePage />} />
              <Route path="/compliance" element={<CompliancePage />} />
              <Route path="/requirements" element={<RequirementsPage />} />
              <Route path="/cca" element={<CCAPage />} />
              <Route path="/fracas" element={<FRACASPage />} />
              <Route path="/components" element={<ComponentsPage />} />
              <Route path="/intelligence" element={<IntelligencePage />} />
              <Route path="/assumptions" element={<AssumptionsPage />} />
              <Route path="/admin" element={<AdminPage />} />
              <Route path="/team" element={<TeamPage />} />
              <Route path="/settings" element={<SettingsPage />} />
              {/* Enterprise ARP 4761A routes (Task 10) */}
              <Route path="/new-project" element={<ProjectSetupWizardPage />} />
              <Route path="/systems" element={<SystemsManagerPage />} />
              <Route path="/dsrs" element={<DSRDashboardPage />} />
              <Route path="/document-templates" element={<DocumentTemplatesPage />} />
              <Route path="/access-control" element={<AccessControlPage />} />
              {/* Stage 3 UI — Sato Dev MBSA 3-view method */}
              <Route path="/functional-view" element={<FunctionalViewPage />} />
              <Route path="/zonal-view" element={<ZonalViewPage />} />
              <Route path="/cut-set-explorer" element={<CutSetExplorerPage />} />
              <Route path="/dal-allocation" element={<DalAllocationPage />} />
            </Routes>
            </ErrorBoundary>
          </Suspense>
        </div>
      </main>
    </div>
  );
}
